import Skeleton, { SkeletonTheme } from 'react-loading-skeleton'
import { EnrollmentJointLayout } from '@egw/layouts'
import { GenericError, ProgressBar, OtpInput, Alert, AlertType, Tag } from '@egw/components'
import { Logger } from '@egw/logger'
import { WEALTH_ROUTES } from '@egw/constants'
import { sendEvent } from '@egw/events'
import { useEffect, useState, type FC } from 'react'
import {
  useGetEnrollmentJointVerifyEmailData,
  useSubmitEnrollmentJointResendEmailData,
  useSubmitEnrollmentJointVerifyEmailData,
} from '@egw/api'
import { useNavigate } from 'react-router'
import { useTranslation } from '@egw/i18n'
import { CheckIcon } from '@heroicons/react/24/outline'
import { passkeyService } from '@egw/auth'
import { UserIcon } from '@heroicons/react/20/solid'
import { PageHeader } from '../../../components'
import { ROUTES as GENERIC_ROUTES } from '../../../constants/routes/routes-generic'
import { ROUTES as JOINT_ROUTES } from '../../../constants/routes/routes-joint'
import { newPathIsDifferent } from '../../../utils/general/new-path-is-different'
import { useValidateRoute } from '../../../hooks/navigation/validate-route'
import { useHandleEnrollmentErrors } from '../../../utils/joint/api-errors/enrollment-error-handler'
import { EDataLayerEventWealth } from '../../../constants/events/EDataLayerEventWealth'
import { JOINT_ENROLLMENT_ERRORS } from '../../../constants/api/error-types-joint'

const log = new Logger('Enrollment - Joint - VerifyEmail')

export const VerifyEmailPage: FC = () => {
  const { t } = useTranslation('features-enrollment')
  const navigate = useNavigate()
  const isMobile = passkeyService.getIsMobile()

  const [isInvalidToken, setIsInvalidToken] = useState(false)
  const [codeResent, setCodeResent] = useState(false)
  const [counter, setCounter] = useState(0)
  const [hasShownIntent, setHasShownIntent] = useState(false)
  const [otpValue, setOtpValue] = useState('')

  const { data, isLoading, error } = useGetEnrollmentJointVerifyEmailData()
  const { mutate, isLoading: isSubmitting, error: submitError } = useSubmitEnrollmentJointVerifyEmailData()
  const {
    mutate: resend,
    isLoading: isResendSubmitting,
    error: submitResendError,
  } = useSubmitEnrollmentJointResendEmailData()

  // Log initial data loading errors
  useEffect(() => {
    if (error) {
      log.error('Error loading enrollment joint verify email data', error)
    }
  }, [error])

  // Log form submission errors
  useEffect(() => {
    if (submitError) {
      log.error('Error submitting enrollment joint verify email form', submitError)
    }
  }, [submitError])

  // Log resend email errors
  useEffect(() => {
    if (submitResendError) {
      log.error('Error resending enrollment joint verification email', submitResendError)
    }
  }, [submitResendError])

  const email = data?.activePerson?.email ?? ''
  const applicationId = data?.activePerson?.applicationId ?? ''
  const progress = data?.activePerson?.progress
  const profileName = data?.activePerson?.name?.firstName ? `${data.activePerson.name.firstName}'s Profile` : ''

  useEffect(() => {
    if (!hasShownIntent && otpValue) {
      setHasShownIntent(true)
      sendEvent(EDataLayerEventWealth.BANKHYCHYS_VERIFYEMAIL_INTENT, {
        applicationId,
      })
    }
  }, [applicationId, otpValue, hasShownIntent])

  // Protect route
  useValidateRoute(data?.activePerson?.path)

  // Error handler for enrollment errors
  const handleEnrollmentErrors = useHandleEnrollmentErrors('Joint - VerifyEmail')

  const handleOtpComplete = (otp: string): void => {
    void onSubmit(otp)
  }

  const handleOtpChange = (value: string): void => {
    const trimmedValue = value.slice(0, 6)
    setOtpValue(trimmedValue)
  }

  const onSubmit = async (emailOtpCode: string): Promise<void> => {
    setIsInvalidToken(false)
    try {
      const response = await mutate({
        activePerson: {
          emailOtpCode,
        },
      })

      // Check for API errors in the response
      if (response && handleEnrollmentErrors(response)) {
        return
      }

      // Handle error response
      if (response?.isError && response.errors.includes(JOINT_ENROLLMENT_ERRORS.INVALID_TOKEN)) {
        setIsInvalidToken(true)
        return
      }
      if (response?.errors.includes('APPLICANT_IN_MANUAL_REVIEW') && response.activePerson?.path) {
        void navigate(response.activePerson.path)
      }

      if (response?.errors.includes('USER_ALREADY_EXISTS') && response.activePerson?.path) {
        void navigate(response.activePerson.path)
      }

      // Handle success - navigate to next step
      if (response?.activePerson?.path && newPathIsDifferent(location.pathname, response.activePerson.path)) {
        if (
          response.activePerson.path.includes(
            JOINT_ROUTES.VERIFY_PHONE.path.split('/').filter(Boolean).pop() || 'verify-phone',
          )
        ) {
          if (!isMobile) {
            await mutate({
              activePerson: {
                triggerPhoneOtp: true,
              },
            })
          } else {
            await mutate({
              activePerson: {
                triggerPhoneMagicLink: true,
              },
            })
          }
          log.info('Navigating to verify phone', response.activePerson.path)
          void navigate(response.activePerson.path)
        }
      } else {
        // If no path from BE, redirect to error
        log.error('No path returned from backend, redirecting to error page')
        void navigate(`${WEALTH_ROUTES.ENROLLMENT.path}/${GENERIC_ROUTES.ERROR.path}`)
      }
    } catch (e) {
      log.error('Error submitting verify email form', e)
    }
  }

  const resendEmail = (): void => {
    setIsInvalidToken(false)
    setCodeResent(true)

    sendEvent(EDataLayerEventWealth.BANKHYCHYS_EMAILOTPRESEND_SUBMIT, {
      applicationId,
    })

    setTimeout(() => {
      void sendMagicLinkEmail()
      setCounter(30)
      const intervalId = setInterval(() => {
        setCounter((prev) => {
          if (prev <= 1) {
            clearInterval(intervalId)
            setCodeResent(false)

            return 0
          }
          return prev - 1
        })
      }, 1000)
    }, 5000)
  }

  const sendMagicLinkEmail = async (): Promise<void> => {
    try {
      await resend({
        activePerson: {
          triggerEmailOtp: true,
        },
      })
    } catch (e) {
      log.error('Error resending verification email', e)
    }
  }

  return (
    <EnrollmentJointLayout title={t('joint.verify_email.page_title')}>
      {/* Loading initial data to make sure the user has access to this screen and retreive data based on user enrollment application */}
      {isLoading ? (
        <div className="w-full">
          <SkeletonTheme baseColor="#eeefee" highlightColor="rgba(255, 255, 255, 0.5)">
            <Skeleton count={1} height="100px" className="mb-10" />
            <Skeleton count={5} height="50px" />
          </SkeletonTheme>
        </div>
      ) : null}

      {/* In some cases initial data can be null, so we assume if it's not loading and there's no error - we show the page */}
      {!isLoading && !error ? (
        <div className="w-full flex-1 flex flex-col justify-start">
          {profileName ? (
            <Tag icon={<UserIcon className="text-olive-400" />} className="mb-xs">
              {profileName}
            </Tag>
          ) : null}
          <PageHeader
            title={t('joint.verify_email.title')}
            // backPath={`${WEALTH_ROUTES.ENROLLMENT.path}/${JOINT_ROUTES.CONTACT_INFO.path}`}
          />
          <ProgressBar showAnimation progressPercentage={progress} className="mb-xl" />
          <p className="typo-primary-base-regular text-text-body mb-3xs">{t('joint.verify_email.description')}</p>
          <p className="typo-primary-base-semibold text-text-header mb-md fs-exclude">{email}</p>
          <p className="typo-primary-base-regular text-text-meta mb-md">{t('joint.verify_email.check_your_email')}</p>
          <OtpInput
            value={otpValue}
            onChange={handleOtpChange}
            onComplete={handleOtpComplete}
            testId="otp-input"
            error={isInvalidToken ? t('joint.verify_email.code_expired') : ''}
          />

          <div className="flex items-baseline gap-4">
            <button
              onClick={resendEmail}
              disabled={isResendSubmitting || codeResent || isSubmitting}
              className={`typo-primary-base-semibold text-left mt-sm w-fit ${codeResent ? 'text-gray-400' : ' text-text-action'}`}
              type="button"
              data-testid="sendEmailOtp_button"
            >
              {t('joint.verify_email.resend_email')}
            </button>
            {codeResent && !counter ? (
              <p className="typo-primary-base-semibold text-text-header-success flex items-center gap-2">
                {t('joint.verify_email.code_sent')} <CheckIcon className="size-5" />
              </p>
            ) : null}
            {counter ? (
              <p className="typo-primary-base-semibold text-text-meta mt-2xs">
                {t('joint.verify_email.send_another_code', {
                  time: counter,
                })}
              </p>
            ) : null}
          </div>

          {submitError ? <Alert alertType={AlertType.Error} description={t('common.generic_error')} className="mt-sm" /> : null}
        </div>
      ) : null}

      {/* This error handles the case when the initial data is not loaded, for example network error */}
      {(error ?? submitResendError) ? (
        <div className="flex-1 flex flex-col justify-start md:justify-center">
          <GenericError errorMessage={t('common.generic_error')} />
        </div>
      ) : null}
    </EnrollmentJointLayout>
  )
}
